"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CallToAction() {
  const {
    ref: captionRef,
    isVisible: captionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: locationsRef,
    isVisible: locationsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const headingLines = [{
    words: ["Elevate"],
    italic: false
  }, {
    words: ["your"],
    italic: false
  }, {
    words: ["industrial", "standards."],
    italic: true,
    italicWord: "industrial"
  }];
  const locations = [{
    name: "HOUSTON"
  }, {
    name: "MIDLAND"
  }, {
    name: "ODESSA"
  }];
  return <section data-section-id="3326" id="call-to-action" className="relative overflow-hidden bg-background text-foreground py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
      backgroundImage: "linear-gradient(hsl(216 40% 20%) 1px, transparent 1px), linear-gradient(90deg, hsl(216 40% 20%) 1px, transparent 1px)",
      backgroundSize: "80px 80px, 80px 80px"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
      backgroundImage: "linear-gradient(hsl(216 40% 20%) 1px, transparent 1px), linear-gradient(90deg, hsl(216 40% 20%) 1px, transparent 1px)",
      backgroundSize: "16px 16px, 16px 16px"
    }} />

      <div className="relative mx-auto w-full max-w-[720px] px-6 text-center">
        {}
        <div ref={captionRef} data-index={0} className={`flex items-center justify-center gap-4 transition-all duration-700 ease-out ${captionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <span className="h-px w-10 sm:w-16 bg-foreground/40" />
          <span className="font-default text-[0.7rem] sm:text-xs tracking-[0.25em] uppercase" style={{
          color: "var(--primary)"
        }}>
            N° 05 — Partnership
          </span>
          <span className="h-px w-10 sm:w-16 bg-foreground/40" />
        </div>

        {}
        <h2 ref={headingRef} className="font-default mt-12 font-serif font-light leading-[0.95] tracking-tight text-foreground" style={{
        fontSize: "clamp(3rem, 6vw, 5.5rem)"
      }}>
          {headingLines.map((line, lineIdx) => <span key={lineIdx} data-index={lineIdx} className="block">
              {line.words.map((word, wordIdx) => {
            const totalDelay = (lineIdx * line.words.length + wordIdx) * 100;
            const isItalic = line.italic && word === line.italicWord;
            return <span key={`${lineIdx}-${wordIdx}`} className={`inline-block transition-all duration-700 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${isItalic ? "italic font-normal" : ""}`} style={{
              transitionDelay: `${totalDelay}ms`
            }}>
                    {word}
                    {wordIdx < line.words.length - 1 ? "\u00A0" : ""}
                  </span>;
          })}
            </span>)}
        </h2>

        {}
        <p ref={bodyRef} className={`mt-10 mx-auto max-w-[560px] text-base sm:text-lg leading-relaxed text-foreground/75 transition-all duration-700 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "400ms"
      }}>
          Whether you are sourcing high-performance materials or seeking technical expertise for your next project, we are ready to engineer the solution.
        </p>

        {}
        <div ref={ctaRef} className={`mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "500ms"
      }}>
          <Button asChild size="lg" className="h-12 px-7 rounded-lg font-default text-xs tracking-[0.2em] uppercase shadow-md hover:opacity-90 transition-opacity" style={{
          backgroundColor: "var(--primary)",
          color: "var(--primary-foreground)"
        }}>
            <Link to="/PhenolicPage">
              View Our Materials
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="h-12 px-7 rounded-lg font-default text-xs tracking-[0.2em] uppercase bg-transparent border border-foreground/60 text-foreground hover:bg-foreground hover:text-background transition-colors">
            <Link to="/ContactPage">Request a Quote</Link>
          </Button>
        </div>

        {}
        <div ref={locationsRef} className={`mt-16 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 transition-all duration-700 ease-out ${locationsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "600ms"
      }}>
          {locations.map((loc, i) => <div key={loc.name} data-index={i} className="flex items-center gap-3">
              <span className="font-default text-[0.7rem] tracking-[0.25em] uppercase text-foreground/55">
                {loc.name}
              </span>
              {i < locations.length - 1 && <span className="font-default text-[0.7rem] text-foreground/30">/</span>}
            </div>)}
        </div>
      </div>
    </section>;
}